import { createStore, createEvent, createEffect, sample } from "effector";
import {
  userService,
  UserBasicInfo,
  UserProfileResponse,
} from "@/features/settings/service/userService";
import {
  logoutFx,
  loginFx,
  googleLoginFx,
  facebookLoginFx,
  linkedinLoginFx,
  initializeAuthFx,
} from "@features/auth/store/auth";

// Events
export const fetchUserBasicInfoRequested = createEvent();
export const fetchUserProfileRequested = createEvent();

// Effects
export const fetchUserBasicInfoFx = createEffect(
  async (): Promise<UserBasicInfo> => {
    return await userService.getBasicInfo();
  }
);

export const fetchUserProfileFx = createEffect(
  async (): Promise<UserProfileResponse> => {
    return await userService.getProfile();
  }
);

// Store
interface UserState {
  basicInfo: UserBasicInfo | null;
  profile: UserProfileResponse | null;
  loading: boolean;
  profileLoading: boolean;
  error: string | null;
}

export const $userStore = createStore<UserState>({
  basicInfo: null,
  profile: null,
  loading: false,
  profileLoading: false,
  error: null,
}).reset(logoutFx.done);

// Store handlers
$userStore
  .on(fetchUserBasicInfoRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchUserBasicInfoFx.doneData, (state, basicInfo) => ({
    ...state,
    basicInfo,
    loading: false,
    error: null,
  }))
  .on(fetchUserBasicInfoFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message || "Failed to fetch user basic info",
  }))
  .on(fetchUserProfileRequested, (state) => ({
    ...state,
    profileLoading: true,
    error: null,
  }))
  .on(fetchUserProfileFx.doneData, (state, profile) => ({
    ...state,
    profile,
    profileLoading: false,
    error: null,
  }))
  .on(fetchUserProfileFx.failData, (state, error) => ({
    ...state,
    profileLoading: false,
    error: error.message || "Failed to fetch user profile",
  }));

// Samples
sample({
  clock: fetchUserBasicInfoRequested,
  target: fetchUserBasicInfoFx,
});

sample({
  clock: fetchUserProfileRequested,
  target: fetchUserProfileFx,
});

// Fetch data after successful auth
sample({
  clock: [
    initializeAuthFx.doneData.filter({ fn: ({ isAuthenticated }) => isAuthenticated }),
    loginFx.doneData,
    googleLoginFx.doneData,
    facebookLoginFx.doneData,
    linkedinLoginFx.doneData,
  ],
  target: [fetchUserBasicInfoRequested, fetchUserProfileRequested],
});


// Derived stores
export const $userPlan = $userStore.map((state) => {
  if (!state.basicInfo) return "Loading...";
  return state.basicInfo.pro_user ? "Pro Plan" : "Standard Plan";
});

export const $userBasicInfo = $userStore.map((state) => state.basicInfo);
export const $userProfile = $userStore.map((state) => state.profile);
export const $userLoading = $userStore.map((state) => state.loading);
export const $userProfileLoading = $userStore.map(
  (state) => state.profileLoading
);
